import type {AttachedDocument} from '@refra/modules/common/types'

export type HeatZonePerformance = {
  heatZone: string
  survivedDays: {
    min: number
    max: number
  }
}

export type MaterialInstallationChartPerformance = {
  unit: string
  values: {
    fromMeter: number
    toMeter: number
    value: number
  }[]
}

export type MaterialInstallationPerformance = {
  heatZones?: HeatZonePerformance[]
  survived?: MaterialInstallationChartPerformance
}

export type MaterialInstallation = {
  id: number
  refractoryId: number
  fromMeter: number
  toMeter: number
  isMortarRequired: boolean
  performance: MaterialInstallationPerformance
  documentsCount?: number
}

export type KilnMaterialInstallation = MaterialInstallation & {
  feedingDate: string
}

export type MaterialInstallationDetails = MaterialInstallation & {
  documents: AttachedDocument[]
  brickCalculation?: BrickCalculationResponse
}

export type AddMaterialInstallationForm = {
  manufacturerId?: number
  refractoryId?: number
  fromMeter: string
  toMeter: string
  isMortarRequired: boolean
}

export type AddMaterialInstallationDTO = Pick<
  MaterialInstallation,
  'fromMeter' | 'toMeter' | 'refractoryId' | 'isMortarRequired'
>

export type EditMaterialInstallationForm = {
  refractoryId: number | null
  fromMeter: string
  toMeter: string
  isMortarRequired: boolean
  brickCalculations?: BrickCalculationForm & {
    sections: BrickCalculationSectionDTO[]
  }
}

export type EditMaterialInstallationDTO = Partial<AddMaterialInstallationDTO> & {
  brickCalculation?: BrickCalculationForm & {
    sections: BrickCalculationSectionDTO[]
  }
}

export type MaterialInstallationBase = {
  fromMeter: number
  toMeter: number
}

export type MaterialInstallationWithColor<T extends MaterialInstallationBase> = T & {
  color: string
}

export type EmptyMaterialInstallationWithColor = MaterialInstallationBase & {
  id: -1
  refractoryId: -1
  isMortarRequired: false
  feedingDate: ''
  color: string
  performance: {
    heatZones: []
  }
  localColor: string
  globalColor: string
  refractoryName: string
  manufacturerName: string
  comments: string[]
}

export interface BrickCalculationForm {
  brickShape1: string
  brickShape2: string
  keyBricksCount: number
  useRefratechnikKeyBricks: boolean
}

export interface BrickShapes {
  id: number
  name: string
}

export enum LiningQuality {
  NotCalculated = 'NotCalculated',
  Optimal = 'Optimal',
  Suboptimal = 'Suboptimal',
  Impossible = 'Impossible'
}

export interface BrickCalculationShape {
  name: string
  brickWeightKg: number
  numberOfBricks: number
  numberOfBricksPerRing: number
  type: string
  weightKg: number
}

export interface BrickCalculationSectionDTO {
  fromMeter: number
  toMeter: number
  layingPattern: string[]
  liningQuantity: LiningQuality
  mixingRatio: string
  numberOfBricks: number
  numberOfRings: number
  weightKg: number
  shapes: BrickCalculationShape[]
}

export interface BrickCalculationResponse {
  containsCone: boolean
  liningQuantity: LiningQuality
  weightKg: number
  numberOfBricks: number
  sections: BrickCalculationSectionDTO[]
}
